/*eslint  @typescript-eslint/no-use-before-define: 0*/
import _ from "lodash";
import {
  addContract,
  editContract,
  getTemporaryComponentFile,
  getTemporaryComponents,
  getTemporaryContract,
  getTemporaryContractor,
  saveContract,
  updateContractTemporary,
  uploadTemporaryComponentFile,
} from "@/requests/contract";
import { DataOptions } from "vuetify";
import { defineModule } from "direct-vuex";
import routerInstance from "@/plugins/router";
import { RELOAD_TYPES } from "@/static/reloadTypes";
import { FILTER_NAMES } from "@/static/filterNames";
import BaseCommand from "@/models/api/commands/BaseCommand";
import { COST_DOCUMENT_TYPES } from "@/static/costDocumentTypes";
import {
  ContractComponentViewModel,
  ContractComponentViewModelResponse,
} from "@/models/api/contracts/editor/ContractComponentViewModel";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { OperationResult } from "@/models/api/orders/OperationResult";
import ContractsEditorCommands from "@/store/modules/contracts/commands";
import { FileViewModel } from "@/models/api/contracts/editor/FileViewModel";
import { ContractViewModel } from "@/models/api/contracts/editor/ContractViewModel";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { ContractorViewModel } from "@/models/api/contracts/editor/ContractorViewModel";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";

export interface ContractsEditorStateInterface {
  sessionId: string | null;
  operationId: string | null;
  type: COST_DOCUMENT_TYPES;
  contractId: number | null;
  contract: ContractViewModel | null;
  contractor: ContractorViewModel | null;

  components: Array<ContractComponentViewModel>;
  componentsCount: number;
  componentsFilters: Array<CriterionItemRequest> | null;
  componentsOptions: DataOptions;
  isComponentsLoading: boolean;
  componentId: string | null;
  file: FileViewModel | null;

  commandQueue: Array<BaseCommand>;
  isQueueProcessing: boolean;
  isAltered: boolean;

  tableHeight: number;
}

export const ContractsEditorState: ContractsEditorStateInterface = {
  sessionId: null,
  operationId: null,
  type: COST_DOCUMENT_TYPES.Other,
  contractId: null,
  contract: null,
  contractor: null,

  components: [],
  componentsCount: 0,
  componentsOptions: new DefaultDataOptions(),
  componentsFilters: null,
  isComponentsLoading: false,
  componentId: null,
  file: null,

  commandQueue: [],
  isQueueProcessing: false,
  isAltered: false,

  tableHeight: 600,
};

const ContractsEditorModule = defineModule({
  modules: {
    commands: ContractsEditorCommands,
  },
  state: ContractsEditorState as ContractsEditorStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setSessionId(state, param: string | null) {
      state.sessionId = param;
    },
    setOperationId(state, param: string | null) {
      state.operationId = param;
    },
    setType(state, param: COST_DOCUMENT_TYPES) {
      state.type = param;
    },
    setContractId(state, param: number | null) {
      state.contractId = param;
    },
    setContract(state, param: ContractViewModel | null) {
      state.contract = param;
    },
    setContractor(state, param: ContractorViewModel | null) {
      state.contractor = param;
    },

    setComponents(state, param: ContractComponentViewModelResponse | null) {
      state.components = param?.items ?? [];
      state.componentsCount = param?.totalCount ?? 0;
    },
    setComponentsOptions(state, param: DataOptions) {
      state.componentsOptions = JSON.parse(JSON.stringify(param));
    },
    setComponentsFilters(state, param: Array<CriterionItemRequest> | null) {
      state.componentsFilters = param;
    },
    setIsComponentsLoading(state, param: boolean) {
      state.isComponentsLoading = param;
    },
    setComponentId(state, param: string | null) {
      state.componentId = param;
    },
    setFile(state, param: FileViewModel | null) {
      state.file = param;
    },

    setIsQueueProcessing(state, param: boolean) {
      state.isQueueProcessing = param;
    },
    setIsAltered(state, param: boolean) {
      state.isAltered = param;
    },

    setTableHeight(state, param: number) {
      state.tableHeight = param;
    },
  },
  actions: {
    async clear(context) {
      const { commit } = _moduleActionContext(context);
      commit.setIsAltered(false);
      commit.setSessionId(null);
      commit.setOperationId(null);
      commit.setType(COST_DOCUMENT_TYPES.Other);
      commit.setContractId(null);
      commit.setContract(null);
      commit.setContractor(null);
      commit.setComponents(null);
      commit.setComponentsFilters(null);
      commit.setComponentsOptions(new DefaultDataOptions());
      commit.setComponentId(null);
      commit.setFile(null);
    },
    async addContract(context, type: COST_DOCUMENT_TYPES) {
      const { commit, dispatch, state } = _moduleActionContext(context);
      await dispatch.clear();
      commit.setType(type);
      const session = await addContract(state.type);
      commit.setSessionId(session);
      commit.setOperationId(session);
      await dispatch.downloadAll();
    },
    async editContract(context, param: { contractId: number; type: COST_DOCUMENT_TYPES }) {
      const { commit, dispatch } = _moduleActionContext(context);
      await dispatch.clear();
      commit.setType(param.type);
      const session = await editContract(param.contractId);
      commit.setSessionId(session);
      commit.setOperationId(session);
      commit.setContractId(param.contractId);
      await dispatch.downloadAll();
    },
    async downloadAll(context) {
      const { dispatch } = _moduleActionContext(context);
      await Promise.all([
        //Download all select items
      ]);
      await Promise.all([
        dispatch.getTemporaryContractor(),
        dispatch.getTemporaryContract(),
        dispatch.getTemporaryComponents(),
        dispatch.getTemporaryComponentFile(),
      ]);
    },
    async getTemporaryContract(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId) commit.setContract(await getTemporaryContract(state.operationId));
    },
    async getTemporaryContractor(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId) commit.setContractor(await getTemporaryContractor(state.operationId));
    },
    async getTemporaryComponents(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId) {
        commit.setIsComponentsLoading(true);
        const response = await getTemporaryComponents(
          {
            count: state.componentsOptions.itemsPerPage,
            page: state.componentsOptions.page - 1,
            criterions: _.cloneDeep(state.componentsFilters),
            filterName: FILTER_NAMES.ContractComponentStandardFilter,
          },
          state.operationId
        );
        commit.setComponents(response);
        commit.setIsComponentsLoading(false);
        if (state.componentId != null && !response.items.find((x) => x.tmpId == state.componentId))
          commit.setComponentId(null);
      }
    },
    async getTemporaryComponentFile(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId && state.componentId)
        commit.setFile(await getTemporaryComponentFile(state.operationId, state.componentId));
    },
    async addFile(context, files: Array<File>) {
      const { dispatch, state } = _moduleActionContext(context);
      if (state.operationId && state.componentId)
        await dispatch.processResponse(await uploadTemporaryComponentFile(state.operationId, state.componentId, files));
    },
    async addCommandToQueue(context, command: BaseCommand) {
      const { state, dispatch } = _moduleActionContext(context);
      state.commandQueue.push(command);
      if (!state.isQueueProcessing) {
        await dispatch.processCommandQueue();
      }
    },
    async processCommandQueue(context) {
      const { commit, state, dispatch } = _moduleActionContext(context);
      if (state.commandQueue.length) {
        commit.setIsQueueProcessing(true);
        try {
          const command = state.commandQueue.shift() as BaseCommand;
          command.SessionId = state.sessionId as string;
          command.OperationId = state.operationId as string;
          const response = await updateContractTemporary(command);
          await dispatch.processResponse(response);
        } finally {
          commit.setIsQueueProcessing(false);
          if (state.commandQueue.length) dispatch.processCommandQueue(); // noinspection ES6MissingAwait
        }
      }
    },
    async processResponse(context, param: OperationResult) {
      const { dispatch, commit } = _moduleActionContext(context);
      commit.setOperationId(param.operationId);
      commit.setIsAltered(true);
      await dispatch.reload(param.reload);
    },
    async reload(context, param: Array<RELOAD_TYPES>) {
      const { dispatch } = _moduleActionContext(context);
      for (const reload of param)
        switch (reload) {
          case RELOAD_TYPES.File:
            await dispatch.getTemporaryComponentFile();
            break;
          case RELOAD_TYPES.Contractor:
            await dispatch.getTemporaryContractor();
            break;
          case RELOAD_TYPES.Details:
            await dispatch.getTemporaryContract();
            break;
          case RELOAD_TYPES.Items:
            await dispatch.getTemporaryComponents();
            break;
        }
    },
    async save(context) {
      const { rootDispatch, state, commit } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (state.operationId) {
        await saveContract(state.operationId);
        await rootDispatch.popup.addSavedOkSnackbar();
        commit.setIsAltered(false);
        switch (state.type) {
          case COST_DOCUMENT_TYPES.Other:
            await routerInstance.push({ name: "Contracts" });
            break;
          case COST_DOCUMENT_TYPES.Commercial:
            await routerInstance.push({ name: "Commercial contracts" });
            break;
        }
      }
    },
  },
});

export default ContractsEditorModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, ContractsEditorModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, ContractsEditorModule);
